import { Context, createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useUser } from './useUser';
import { EmploymentTypeInfoOption, getInfo, InfoOption } from '../networkRequest/infoService';

type DisplayNames = Record<string, string>;

export type EmploymentTypeOption = {
  value: string;
  label: string;
};

export type InfoContextType = {
  loading: boolean;
  unclearedReasonDisplayName: (value: string | null, agencyName: string) => string | null;
  // The employment types (designations) an official can be assigned
  employmentTypeOptions: EmploymentTypeOption[];
  employmentTypeDisplayName: (value: string | null) => string | null;
};

// Show the raw value when there is no display name for it
const lookupDisplayName = (displayNames: DisplayNames | null, value: string | null): string | null => {
  if (value === null) {
    return null;
  }
  return displayNames?.[value] ?? value;
};

const buildDisplayNames = (options: InfoOption[]): DisplayNames => {
  return Object.fromEntries(options.map(option => [option.value, option.display_name]));
};

export const InfoContext: Context<InfoContextType> = createContext<InfoContextType>({
  loading: false,
  unclearedReasonDisplayName: (value) => value,
  employmentTypeOptions: [],
  employmentTypeDisplayName: (value) => value,
});

// Fetches the static /info data once the user is authenticated and keeps it in memory.
// A failed fetch is fatal: the error is thrown during render so the root ErrorBoundary shows it.
export const InfoProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useUser();
  const [unclearedReasonDisplayNames, setUnclearedReasonDisplayNames] = useState<DisplayNames | null>(null);
  const [employmentTypes, setEmploymentTypes] = useState<EmploymentTypeInfoOption[]>([]);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Only fetch once. The user object changes when the access token refreshes,
    // but the info data is static so there's no need to fetch it again.
    if (!user || unclearedReasonDisplayNames !== null) return;

    const fetchInfo = async () => {
      const resp = await getInfo(user.accessToken);
      if (resp.data === undefined) {
        setError(new Error(`Failed to fetch info: ${resp.error}`));
        return;
      }
      setEmploymentTypes(resp.data.employment_types);
      setUnclearedReasonDisplayNames(buildDisplayNames(resp.data.uncleared_reasons));
    };
    fetchInfo();
  }, [user, unclearedReasonDisplayNames]);

  // Memoized so pages can safely list it as an effect dependency.
  // Some display names contain an {agency_name} placeholder for the agency official's agency.
  const unclearedReasonDisplayName = useCallback(
    (value: string | null, agencyName: string) => {
      const displayName = lookupDisplayName(unclearedReasonDisplayNames, value);
      if (displayName === null) {
        return null;
      }
      return displayName.replaceAll('{agency_name}', agencyName);
    },
    [unclearedReasonDisplayNames]
  );

  const employmentTypeOptions = useMemo(
    () => employmentTypes
      .filter(employmentType => !employmentType.deprecated)
      .map(employmentType => ({ value: employmentType.value, label: employmentType.display_name })),
    [employmentTypes]
  );

  const employmentTypeDisplayNames = useMemo(() => buildDisplayNames(employmentTypes), [employmentTypes]);

  const employmentTypeDisplayName = useCallback(
    (value: string | null) => lookupDisplayName(employmentTypeDisplayNames, value),
    [employmentTypeDisplayNames]
  );

  if (error) {
    throw error;
  }

  const loading = user !== null && unclearedReasonDisplayNames === null;

  return (
    <InfoContext.Provider
      value={{ loading, unclearedReasonDisplayName, employmentTypeOptions, employmentTypeDisplayName }}
    >
      {children}
    </InfoContext.Provider>
  );
};

export const useInfo = () => useContext(InfoContext);
